import { useState } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ItemsEditor, AddressFields, STATUSES, emptyAddr } from "@/pages/admin/ManualOrderForm";
import { toast } from "sonner";

export default function OrderEditForm({ order, products, onDone }) {
  const [c, setC] = useState({ ...order.customer });
  const [billing, setBilling] = useState({ ...emptyAddr, ...order.billing_address });
  const [delivery, setDelivery] = useState({ ...emptyAddr, ...order.delivery_address });
  const [items, setItems] = useState(order.items.map((i) => ({
    product_id: i.product_id || "", sku: i.sku, name: i.name, unit_price: i.unit_price, qty: i.qty,
  })));
  const [shippingOverride, setShippingOverride] = useState(order.free_shipping ? 0 : order.shipping);
  const [status, setStatus] = useState(order.status);
  const [notes, setNotes] = useState(order.notes || "");
  const [busy, setBusy] = useState(false);

  const liveTotal = items.reduce((s, i) => s + (Number(i.unit_price) || 0) * (Number(i.qty) || 0), 0)
    + (Number(shippingOverride) || 0);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.order_number}`, {
        customer: c, billing_address: billing, delivery_address: delivery, notes,
        items: items.map((i) => ({
          product_id: i.product_id || null, sku: i.sku || null, name: i.name || null,
          unit_price: i.unit_price === "" ? null : Number(i.unit_price), qty: Number(i.qty || 1),
        })),
        shipping_override: shippingOverride === "" ? null : Number(shippingOverride),
        status,
      });
      toast.success(`Order ${data.order_number} updated`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update order"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Customer Name</Label><Input data-testid="eo-name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} /></div>
        <div><Label>Company</Label><Input value={c.company || ""} onChange={(e) => setC({ ...c, company: e.target.value })} /></div>
        <div><Label>Email</Label><Input data-testid="eo-email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></div>
        <div><Label>Phone</Label><Input value={c.phone || ""} onChange={(e) => setC({ ...c, phone: e.target.value })} /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <AddressFields label="Billing Address" addr={billing} onChange={setBilling} prefix="eo-bill" />
        <AddressFields label="Delivery Address" addr={delivery} onChange={setDelivery} prefix="eo-del" />
      </div>
      <ItemsEditor products={products} items={items} setItems={setItems} />
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Shipping (editable)</Label><Input data-testid="eo-shipping" type="number" step="0.01" value={shippingOverride} onChange={(e) => setShippingOverride(e.target.value)} /></div>
        <div><Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="eo-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">New total (incl shipping): <strong className="font-mono text-foreground">{money(liveTotal)}</strong></span>
        <Button data-testid="eo-save" onClick={save} disabled={busy} className="bg-steel text-white hover:bg-steel-light">
          {busy ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
